import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  Camera,
  Check,
  Loader2,
  Clock,
  AlertCircle,
  Package,
  X,
} from 'lucide-react';
import { orders as ordersApi, products as productsApi } from '@/utils/api';
import { Button, Input, Select, Card, Spinner, Modal } from '@/components/common';
import { toast } from 'sonner';

// Simple UUID generator (fallback for browsers without crypto.randomUUID)
const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const OrderForm = () => {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'expired', 'not_found', 'network'
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recentProducts, setRecentProducts] = useState([]);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualProduct, setManualProduct] = useState({
    name: '',
    quantity: 1,
    unit: 'Stück',
    notes: '',
  });
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const saveTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    loadOrder();
  }, [token]);

  const loadOrder = async () => {
    try {
      const response = await ordersApi.getByToken(token);
      setOrder(response.data.order);
      setProjects(response.data.projects || []);

      // Check if order was already submitted
      if (response.data.order?.status !== 'draft') {
        setSubmitted(true);
      }

      // Load recent products if we have user_id
      if (response.data.order?.created_by_user_id) {
        try {
          const historyRes = await ordersApi.getOrderHistory(
            response.data.order.created_by_user_id
          );
          setRecentProducts(historyRes.data || []);
        } catch {
          // Ignore history errors
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.expired) {
        setError('Dieser Link ist abgelaufen. Bitte fordern Sie einen neuen Link an.');
        setErrorType('expired');
      } else if (err.response?.status === 404) {
        setError('Bestellung nicht gefunden oder Link ungültig.');
        setErrorType('not_found');
      } else {
        setError('Verbindungsproblem. Bitte versuchen Sie es später erneut.');
        setErrorType('network');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format expiry date (handles Firestore Timestamps and ISO strings)
  const formatExpiryDate = (dateValue) => {
    if (!dateValue) return null;

    let date;

    // Handle Firestore Timestamp object
    if (dateValue._seconds !== undefined) {
      date = new Date(dateValue._seconds * 1000);
    } else if (dateValue.seconds !== undefined) {
      date = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else {
      date = new Date(dateValue);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return null;

    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper to convert Firestore timestamp to Date
  const toDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue._seconds !== undefined) return new Date(dateValue._seconds * 1000);
    if (dateValue.seconds !== undefined) return new Date(dateValue.seconds * 1000);
    if (typeof dateValue.toDate === 'function') return dateValue.toDate();
    return new Date(dateValue);
  };

  // Check if order is expiring soon (within 2 hours)
  const isExpiringSoon = () => {
    if (!order?.expires_at) return false;
    const expiresAt = toDate(order.expires_at);
    if (!expiresAt || isNaN(expiresAt.getTime())) return false;
    const now = new Date();
    const twoHours = 2 * 60 * 60 * 1000;
    return expiresAt - now < twoHours && expiresAt > now;
  };

  // Debounced save
  const saveOrder = useCallback(
    async (updatedOrder) => {
      setSaving(true);
      try {
        await ordersApi.updateByToken(token, {
          project_id: updatedOrder.project_id,
          project_name: updatedOrder.project_name,
          items: updatedOrder.items,
        });
      } catch (err) {
        if (err.response?.data?.expired) {
          setError('Dieser Link ist abgelaufen.');
          setErrorType('expired');
        } else {
          toast.error('Fehler beim Speichern');
        }
      } finally {
        setSaving(false);
      }
    },
    [token]
  );

  const debouncedSave = useCallback(
    (updatedOrder) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveOrder(updatedOrder);
      }, 2000);
    },
    [saveOrder]
  );

  // Search products with debounce
  const handleSearch = (query) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await productsApi.search(query);
        setSearchResults(response.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300); // 300ms debounce
  };

  // Add product to order
  const addProduct = (product, quantity = 1) => {
    if (!order) {
      toast.error('Fehler: Bestellung nicht geladen');
      return;
    }

    const productName = product.name || product.product_name;
    if (!productName) {
        toast.error('Fehler: Artikelname fehlt');
      return;
    }

    const newItem = {
      id: generateId(),
      product_id: product.id || product.product_id || null,
      product_name: productName,
      article_number: product.article_number || '',
      quantity: parseInt(quantity) || 1,
      unit: product.unit || 'Stück',
      notes: product.notes || '',
      photo_urls: [],
      supplier_links: product.supplier_links || [],
      is_checked: false,
    };

    const updatedOrder = {
      ...order,
      items: [...(order.items || []), newItem],
    };

    setOrder(updatedOrder);
    debouncedSave(updatedOrder);
    setSearchQuery('');
    setSearchResults([]);
    setManualEntry(false);
    setManualProduct({ name: '', quantity: 1, unit: 'Stück', notes: '' });
    toast.success(`${newItem.product_name} hinzugefügt`);
  };

  // Update item
  const updateItem = (itemId, field, value) => {
    const updatedItems = order.items.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );

    const updatedOrder = { ...order, items: updatedItems };
    setOrder(updatedOrder);
    debouncedSave(updatedOrder);
  };

  // Remove item
  const removeItem = (itemId) => {
    const updatedOrder = {
      ...order,
      items: order.items.filter((item) => item.id !== itemId),
    };
    setOrder(updatedOrder);
    debouncedSave(updatedOrder);
    setShowConfirmDelete(null);
    toast.success('Position entfernt');
  };

  // Change project
  const handleProjectChange = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    const updatedOrder = {
      ...order,
      project_id: projectId,
      project_name: project?.name || null,
    };
    setOrder(updatedOrder);
    debouncedSave(updatedOrder);
  };

  // Submit order
  const handleSubmit = async () => {
    setShowConfirmSubmit(false);
    setSubmitting(true);
    try {
      await ordersApi.submitByToken(token);
      setSubmitted(true);
      toast.success('Bestellung erfolgreich abgesendet!');
    } catch (err) {
      if (err.response?.data?.expired) {
        setError('Dieser Link ist abgelaufen.');
        setErrorType('expired');
      } else {
        toast.error(err.message || 'Fehler beim Absenden');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Validate before showing confirm dialog
  const validateAndConfirm = () => {
    if (!order.project_id) {
      toast.error('Bitte wählen Sie ein Bauvorhaben');
      return;
    }

    if (!order.items || order.items.length === 0) {
      toast.error('Bitte fügen Sie mindestens eine Position hinzu');
      return;
    }

    setShowConfirmSubmit(true);
  };

  // Handle manual entry
  const handleManualAdd = () => {
    const trimmedName = manualProduct.name?.trim();
    if (!trimmedName) {
      toast.error('Bitte geben Sie einen Artikelnamen ein');
      return;
    }

    // Ensure quantity is a valid number (at least 1)
    const quantity = parseInt(manualProduct.quantity) || 1;

    const productToAdd = {
      name: trimmedName,
      quantity: quantity,
      unit: manualProduct.unit || 'Stück',
      notes: manualProduct.notes || '',
    };

    addProduct(productToAdd, quantity);
  };

  const unitOptions = [
    { value: 'Stück', label: 'Stück' },
    { value: 'Meter', label: 'Meter' },
    { value: 'Karton', label: 'Karton' },
    { value: 'Sack', label: 'Sack' },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Bestellung wird geladen...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md text-center p-8">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              errorType === 'expired' ? 'bg-orange-100' : 'bg-red-100'
            }`}
          >
            {errorType === 'expired' ? (
              <Clock className="w-8 h-8 text-orange-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h1
            className={`text-xl font-semibold mb-2 ${
              errorType === 'expired' ? 'text-orange-600' : 'text-red-600'
            }`}
          >
            {errorType === 'expired' ? 'Link abgelaufen' : 'Fehler'}
          </h1>
          <p className="text-gray-600">{error}</p>
          {errorType === 'network' && (
            <Button variant="primary" onClick={loadOrder} className="mt-4">
              Erneut versuchen
            </Button>
          )}
        </Card>
      </div>
    );
  }

  // Success state (order submitted)
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md text-center p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Bestellung erfolgreich gesendet!
          </h1>
          <p className="text-gray-600 mb-6">
            Ihre Bestellung mit {order?.items?.length || 0} Position(en) wurde übermittelt.
            Sie erhalten eine Benachrichtigung sobald bestellt wurde.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-500">Bauvorhaben</p>
            <p className="font-medium">{order?.project_name || '-'}</p>
          </div>
          <p className="text-sm text-gray-400 mt-6">Sie können diese Seite jetzt schließen.</p>
        </Card>
      </div>
    );
  }

  // Main order form
  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <header className="bg-primary-500 text-white sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="COMPEER" className="h-8 w-auto bg-white rounded p-1" />
            <div>
              <h1 className="text-lg font-bold">Bestellung</h1>
              {order?.project_name && (
                <p className="text-sm text-primary-200">{order.project_name}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Expiry Warning / Status Bar */}
      <div
        className={`border-b px-4 py-2 sticky top-[60px] z-30 ${
          isExpiringSoon() ? 'bg-orange-50 border-orange-200' : 'bg-white'
        }`}
      >
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          {/* Save Status */}
          <div className="flex items-center gap-2 text-sm">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                <span className="text-gray-500">Speichern...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-gray-500">Gespeichert</span>
              </>
            )}
          </div>

          {/* Expiry Info */}
          {order?.expires_at && (
            <div
              className={`flex items-center gap-1 text-sm ${
                isExpiringSoon() ? 'text-orange-600 font-medium' : 'text-gray-500'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>Gültig bis: {formatExpiryDate(order.expires_at)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Project Selection */}
        <Card className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bauvorhaben auswählen *
          </label>
          <Select
            value={order?.project_id || ''}
            onChange={(e) => handleProjectChange(e.target.value)}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            placeholder="Bauvorhaben wählen..."
          />
        </Card>

        {/* Search Products */}
        <Card className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Search className="w-4 h-4 inline mr-1" />
            Artikel suchen
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Artikelname oder Artikelnummer..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {/* Search Results */}
          {searchQuery.length >= 2 && (
            <div className="mt-2 border rounded-lg divide-y max-h-64 overflow-y-auto">
              {searching ? (
                <div className="p-4 text-center">
                  <Spinner size="sm" />
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product)}
                    className="w-full p-3 text-left hover:bg-gray-50 flex items-center justify-between transition-colors"
                  >
                    <div>
                      <p className="font-medium">{product.name}</p>
                      {product.article_number && (
                        <p className="text-sm text-gray-500">Art.Nr.: {product.article_number}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-primary-500">
                      <span className="text-sm">Hinzufügen</span>
                      <Plus className="w-5 h-5" />
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">Keine Produkte gefunden</div>
              )}
            </div>
          )}

          {/* Manual Entry Toggle */}
          <button
            onClick={() => setManualEntry(!manualEntry)}
            className="mt-3 flex items-center gap-2 text-sm text-primary-500 hover:text-primary-700"
          >
            <Plus className="w-4 h-4" />
            {manualEntry ? 'Suche verwenden' : 'Manuell hinzufügen'}
          </button>

          {/* Manual Entry Form */}
          {manualEntry && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <Input
                placeholder="Artikelname *"
                value={manualProduct.name}
                onChange={(e) => setManualProduct({ ...manualProduct, name: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  min="1"
                  placeholder="Menge"
                  value={manualProduct.quantity || ''}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) =>
                    setManualProduct({
                      ...manualProduct,
                      quantity: e.target.value === '' ? '' : parseInt(e.target.value) || 1,
                    })
                  }
                  onBlur={(e) => {
                    if (!e.target.value || parseInt(e.target.value) < 1) {
                      setManualProduct({ ...manualProduct, quantity: 1 });
                    }
                  }}
                />
                <Select
                  value={manualProduct.unit}
                  onChange={(e) => setManualProduct({ ...manualProduct, unit: e.target.value })}
                  options={unitOptions}
                  placeholder=""
                />
              </div>
              <Input
                placeholder="Notiz (optional)"
                value={manualProduct.notes}
                onChange={(e) => setManualProduct({ ...manualProduct, notes: e.target.value })}
              />
              <Button type="button" variant="primary" onClick={handleManualAdd} className="w-full">
                Hinzufügen
              </Button>
            </div>
          )}
        </Card>

        {/* Recent Products */}
        {recentProducts.length > 0 && (
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Zuletzt bestellt</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {recentProducts.slice(0, 10).map((product) => (
                <button
                  key={product.id}
                  onClick={() => addProduct(product)}
                  className="px-3 py-2 bg-gray-100 hover:bg-primary-50 hover:text-primary-600 rounded-full text-sm transition-colors flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  {product.product_name}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Current Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" />
              Positionen ({order?.items?.length || 0})
            </h2>
          </div>

          {!order?.items || order.items.length === 0 ? (
            <Card className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Noch keine Artikel hinzugefügt</p>
              <p className="text-sm text-gray-400 mt-1">
                Suchen Sie nach Artikeln oder fügen Sie manuell hinzu
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <Card key={item.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{item.product_name}</p>
                      {item.article_number && (
                        <p className="text-sm text-gray-500">Art.Nr.: {item.article_number}</p>
                      )}

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={() =>
                            updateItem(item.id, 'quantity', Math.max(1, item.quantity - 1))
                          }
                          className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) =>
                            updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)
                          }
                          className="w-16 text-center border rounded-lg px-2 py-2 font-medium"
                        />
                        <button
                          onClick={() => updateItem(item.id, 'quantity', item.quantity + 1)}
                          className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <Select
                          value={item.unit}
                          onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                          options={unitOptions}
                          placeholder=""
                          className="flex-1"
                        />
                      </div>

                      {/* Notes */}
                      <input
                        type="text"
                        placeholder="Notiz hinzufügen..."
                        value={item.notes || ''}
                        onChange={(e) => updateItem(item.id, 'notes', e.target.value)}
                        className="mt-3 w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />

                      {/* Photo Button */}
                      <button className="mt-2 flex items-center gap-1 text-sm text-gray-500 hover:text-primary-500">
                        <Camera className="w-4 h-4" />
                        Foto hinzufügen
                      </button>
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={() => setShowConfirmDelete(item.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer - Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50">
        <div className="max-w-2xl mx-auto p-4">
          <Button
            variant="primary"
            onClick={validateAndConfirm}
            loading={submitting}
            disabled={!order?.items?.length || !order?.project_id || submitting}
            className="w-full py-4 text-lg font-semibold"
          >
            Bestellung absenden ({order?.items?.length || 0})
          </Button>
        </div>
      </div>

      {/* Confirm Submit Modal */}
      <Modal
        isOpen={showConfirmSubmit}
        onClose={() => setShowConfirmSubmit(false)}
        title="Bestellung absenden?"
        size="sm"
      >
        <div className="p-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-primary-600" />
            </div>
            <p className="text-gray-600">
              Möchten Sie diese Bestellung mit{' '}
              <span className="font-semibold">{order?.items?.length} Position(en)</span> absenden?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Bauvorhaben: <span className="font-medium">{order?.project_name}</span>
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirmSubmit(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              className="flex-1"
            >
              Absenden
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal
        isOpen={!!showConfirmDelete}
        onClose={() => setShowConfirmDelete(null)}
        title="Position entfernen?"
        size="sm"
      >
        <div className="p-4">
          <p className="text-gray-600 mb-6">
            Möchten Sie diese Position wirklich aus der Bestellung entfernen?
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDelete(null)}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => removeItem(showConfirmDelete)}
              className="flex-1"
            >
              Entfernen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderForm;
